import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { FadeUp, SectionHeader } from "./Reveal.jsx";
import { useSiteContent } from "@/content/ContentContext.jsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PURPOSES = [
  "Family Weekend Stay & Get-Together",
  "Corporate Meeting & Stay",
  "Birthday & Family Celebration",
  "Film / TV Series Shooting",
  "Ad Film Shooting",
  "Other",
];

const today = () => new Date().toISOString().split("T")[0];

const inputCls =
  "w-full rounded-xl border hairline bg-white px-4 py-3 text-sm text-ink outline-none transition-all duration-200 placeholder:text-zinc-400 focus:border-brand focus:ring-4 focus:ring-brand/10";

const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-brand";

const BookingForm = () => {
  const { content } = useSiteContent();
  const c = content.booking;
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "", purpose: "",
    num_persons: "", check_in: "", check_out: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.check_out && form.check_in && form.check_out <= form.check_in) {
      toast.error("Check-out date must be after the check-in date.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        ...form,
        num_persons: Number(form.num_persons),
      });
      toast.success(`Request sent! Reference ${data.booking_id}. We'll reach you on ${form.phone} shortly.`, {
        duration: 6000,
        data: { testid: "booking-success-toast" },
      });
      setForm({ name: "", phone: "", email: "", address: "", purpose: "", num_persons: "", check_in: "", check_out: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your request. Please call +91 99363 56991.", {
        duration: 6000,
        data: { testid: "booking-error-toast" },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="relative py-20 sm:py-28 lg:py-36" data-testid="booking-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-10">
        <SectionHeader eyebrow={c.eyebrow} title={c.title} lead={c.lead} testid="booking-eyebrow" />

        <div className="mt-14 grid gap-8 lg:grid-cols-5">
          <FadeUp className="lg:col-span-2" delay={0.05}>
            <div className="grain relative flex h-full min-h-[320px] flex-col justify-between overflow-hidden rounded-3xl bg-brand p-8 text-white sm:p-10">
              <div>
                <p lang="ta" className="font-tamil text-base text-brand-tint2/90" data-testid="booking-side-tamil">
                  {c.sideTamil}
                </p>
                <h3 className="mt-4 font-display text-3xl leading-snug">{c.sideTitle}</h3>
                <p className="mt-4 text-sm leading-relaxed text-white/75">{c.sideBody}</p>
              </div>
              <div className="mt-10 space-y-4">
                <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 text-sm transition-colors hover:text-brand-tint2" data-testid="booking-side-phone">
                  <Phone size={16} className="text-brand-tint2" /> {c.phone}
                </a>
                <a href={`mailto:${c.email}`} className="flex items-center gap-3 text-sm transition-colors hover:text-brand-tint2" data-testid="booking-side-email">
                  <Mail size={16} className="text-brand-tint2" /> {c.email}
                </a>
                <p className="flex items-center gap-3 text-xs text-white/55" data-testid="booking-side-notice">
                  <CheckCircle2 size={14} /> {c.notice}
                </p>
              </div>
            </div>
          </FadeUp>

          <FadeUp className="lg:col-span-3" delay={0.12}>
            <form
              onSubmit={submit}
              className="rounded-3xl border hairline bg-white p-7 shadow-[0_20px_60px_rgba(7,58,30,0.07)] sm:p-10"
              data-testid="booking-form"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelCls} htmlFor="bk-name">Full Name *</label>
                  <input id="bk-name" required minLength={2} className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} data-testid="booking-name-input" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-phone">Contact Number *</label>
                  <input id="bk-phone" required type="tel" className={inputCls} placeholder="+91 ..." value={form.phone} onChange={set("phone")} data-testid="booking-phone-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="bk-email">Email Address *</label>
                  <input id="bk-email" required type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={set("email")} data-testid="booking-email-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="bk-address">Location &amp; Address *</label>
                  <textarea id="bk-address" required rows={2} className={`${inputCls} resize-none`} placeholder="City / full address" value={form.address} onChange={set("address")} data-testid="booking-address-input" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-purpose">Purpose of Booking *</label>
                  <select id="bk-purpose" required className={inputCls} value={form.purpose} onChange={set("purpose")} data-testid="booking-purpose-select">
                    <option value="" disabled>Select purpose</option>
                    {PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-persons">Number of Persons *</label>
                  <input id="bk-persons" required type="number" min={1} max={500} className={inputCls} placeholder="e.g. 12" value={form.num_persons} onChange={set("num_persons")} data-testid="booking-persons-input" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-checkin">Check-in Date *</label>
                  <input id="bk-checkin" required type="date" min={today()} className={inputCls} value={form.check_in} onChange={set("check_in")} data-testid="booking-checkin-input" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-checkout">Check-out Date *</label>
                  <input id="bk-checkout" required type="date" min={form.check_in || today()} className={inputCls} value={form.check_out} onChange={set("check_out")} data-testid="booking-checkout-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="bk-message">Message (optional)</label>
                  <textarea id="bk-message" rows={3} className={`${inputCls} resize-none`} placeholder="Anything we should know — food, decor, shoot requirements…" value={form.message} onChange={set("message")} data-testid="booking-message-input" />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-brand py-4 text-sm font-semibold uppercase tracking-[0.16em] text-white transition-all duration-300 hover:bg-brand-deep hover:shadow-[0_14px_34px_rgba(13,91,49,0.35)] disabled:cursor-not-allowed disabled:opacity-70"
                data-testid="booking-submit-button"
              >
                {submitting ? (
                  <><Loader2 size={18} className="animate-spin" /> Sending your request…</>
                ) : (
                  <>Send Booking Request</>
                )}
              </button>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

export default BookingForm;
